<template>
    <div v-if="form.WorkOrderId" class="alert alert-info d-flex align-items-center mb-3 py-2">
        <i class="fas fa-lock me-2"></i>
        <span>This item is linked to <a href="javascript:void(0);" @click="$router.push('/invoices/edit/' + form.WorkOrderId)"><strong>Invoice {{ form.WorkOrder?.InvoiceNumber || '#' + form.WorkOrderId }}</strong></a> and cannot be edited.</span>
    </div>
    <fieldset :disabled="!!form.WorkOrderId">
    <div class="row">
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Technician</span>
                    <tech-ac 
                        v-model="form.AssignedTo" 
                        placeholder="Search for a technician..."
                    ></tech-ac>
                </label>
            </div>
        </div>
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Client</span>
                    <client-ac 
                        v-model="form.CustomerNumber" 
                        inputclass="form-control form-control-sm" 
                        placeholder="Search for a client..."
                    ></client-ac>
                </label>
            </div>
        </div>
        <div class="col-md-4">
            <div class="d-block mb-3">
                <label class="d-block">
                    <span class="form-label">Request/Job</span>
                    <job-ac 
                        v-model="form.RequestId" 
                        :client="form.CustomerNumber"
                        placeholder="Search for a request..."
                    ></job-ac>
                </label>
            </div>
        </div>
    </div>

    <div class="row">
        <div class="col-md-3">
            <label class="d-block mb-3">
                <span>Work Date</span>
                <input type="date" 
                    class="form-control form-control-sm" 
                    v-model="form.WorkCompleteDate" 
                    placeholder="Work Date"
                >
            </label>
        </div>
        <div class="col-md-3">
            <label class="d-block mb-3">
                <span>Hours Worked</span>
                <input 
                    class="form-control form-control-sm" 
                    type="number" 
                    step="0.25"
                    v-model="form.ActualTime" 
                    placeholder="Hours" 
                />
            </label>
        </div>
        <div class="col-md-3">
            <label class="d-block mb-3">
                <span>Should Bill ($)</span>
                <div class="input-group input-group-sm">
                    <span class="input-group-text">$</span>
                    <input 
                        class="form-control form-control-sm" 
                        type="number" 
                        step="0.01"
                        v-model="form.ShouldBill" 
                        placeholder="0.00" 
                    />
                </div>
            </label>
        </div>
        <div class="col-md-3">
            <label class="d-block mb-3">
                <span>Status</span>
                <select 
                    class="form-select form-select-sm" 
                    v-model="form.Status"
                >
                    <option :value="undefined">Select Status...</option>
                    <option 
                        v-for="status in statusOptions" 
                        :key="status.value" 
                        :value="status.value"
                    >
                        {{ status.label }}
                    </option>
                </select>
            </label>
        </div>
    </div>

    <div class="row">
        <div class="col-md-6">
            <label class="d-block mb-3">
                <span>Location</span>
                <select 
                    class="form-select form-select-sm" 
                    v-model="form.TaskLocation"
                >
                    <option :value="undefined">Select Location...</option>
                    <option 
                        v-for="loc in locationOptions" 
                        :key="loc.value" 
                        :value="loc.value"
                    >
                        {{ loc.label }}
                    </option>
                </select>
            </label>
        </div>
        <div class="col-md-3">
            <label class="d-block mb-3">
                <span>Invoice Amount ($)</span>
                <div class="input-group input-group-sm">
                    <span class="input-group-text">$</span>
                    <input 
                        class="form-control form-control-sm" 
                        type="number" 
                        step="0.01"
                        v-model="form.InvoiceAmount" 
                        placeholder="0.00" 
                    />
                </div>
            </label>
        </div>
        <div class="col-md-3">
            <label class="d-block mb-3">
                <span>Invoice Time (hrs)</span>
                <input 
                    class="form-control form-control-sm" 
                    type="number" 
                    step="0.25"
                    v-model="form.InvoiceTime" 
                    placeholder="0.00" 
                />
            </label>
        </div>
    </div>

    <div class="row">
        <div class="col-12">
            <label class="d-block mb-3">
                <span>Work Description</span>
                <textarea class="form-control form-control-sm" placeholder="Describe the work performed" v-model="form.ActualWork"
                    rows="4"></textarea>
            </label>
        </div>
    </div>

    <div class="row">
        <div class="col-12">
            <label class="d-block mb-3">
                <span>Invoice Text</span>
                <textarea class="form-control form-control-sm" placeholder="Text to appear on invoice..." v-model="form.InvoiceText"
                    rows="2"></textarea>
            </label>
        </div>
    </div>
    </fieldset>
</template>

<script lang="ts" setup>
import { computed } from "vue";
import TechAc from "@/components/Techs/TechAc.vue";
import ClientAc from "@/components/Clients/ClientAc.vue";
import JobAc from "@/components/Jobs/JobAc.vue";
import { storeToRefs } from "pinia";
import { useTaskStatusStore } from "@/stores/TaskStatusStore";

const taskStatusStore = useTaskStatusStore();
const { list: taskStatusList } = storeToRefs(taskStatusStore);

// Convert store list to options array for select dropdown
const statusOptions = computed(() => 
    Object.values(taskStatusList.value).map(status => ({
        value: status.Id,
        label: status.Status
    }))
);

const locationOptions = [
    { value: 1, label: "ON-SITE" },
    { value: 2, label: "GTA" },
    { value: 3, label: "WEB" },
    { value: 4, label: "PHONE/EMAIL" },
    { value: 5, label: "IN HOUSE" },
    { value: 6, label: "OVERSIGHT" }
];

const model = defineModel<any>();

const form = computed({
    get: () => {
        if (model.value && model.value.Type !== 'work') {
            model.value.Type = 'work';
        }
        return model.value || {};
    },
    set: (value) => {
        if (value) {
            value.Type = 'work';
        }
        model.value = value;
    }
});
</script>
